import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MarkDownFormatter } from '@/common';
import { useCanvasContext } from '../hooks/useCanvasContext';
import {
  summaryQueryOptions,
  refreshSummaryMutationOptions,
} from '../api/queries/salesforceSummary';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { Loader } from '@/components/ui/loader';
export default function SummaryPage() {
  const { t: tSummary } = useTranslation('summary');
  const userTimezone = useUserTimezone();
  const queryClient = useQueryClient();

  const { isCanvas } = useCanvasContext();
  const { objectName, recordId } = useParams<{ objectName?: string; recordId?: string }>();

  const { data, isLoading, error } = useQuery(summaryQueryOptions(objectName, recordId));

  const { mutate: refreshSummary, isPending: isRefreshing } = useMutation({
    ...refreshSummaryMutationOptions(objectName, recordId),
    onSuccess: newData => {
      // Update the cache with the new data
      queryClient.setQueryData(['summary', objectName, recordId], newData);
      toast.success(tSummary('summaryRefreshed'));
    },
    onError: error => {
      toast.error(tSummary('errorRefreshingSummary', { message: error.message }), {
        richColors: true,
      });
    },
  });

  useEffect(() => {
    if (error) {
      toast.error(tSummary('errorLoadingSummary', { message: error.message }), {
        richColors: true,
      });
    }
  }, [error, tSummary]);

  const handleRefresh = () => {
    refreshSummary();
  };

  if (!recordId || !objectName) {
    return (
      <div className={`${isCanvas ? 'p-4 h-full' : 'p-6'}`}>
        <h1 className={`${isCanvas ? 'text-xl' : 'text-2xl'} font-bold mb-4`}>
          {isCanvas ? tSummary('summaryCanvas') : tSummary('summaryPage')}
        </h1>
        <p>{tSummary('noRecordIdMessage')}</p>
        <p className="text-sm text-gray-600 mt-2">{tSummary('noRecordIdNavigation')}</p>
      </div>
    );
  }

  return (
    <div className={`${isCanvas ? 'h-full' : ''} bg-white w-full flex flex-col`}>
      <>
        <div className="flex p-2 items-center">
          <h1 className="text-sm font-semibold text-gray-900">Summary</h1>
          <Button
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            variant="outline"
            size="sm"
            className="ml-4 gap-2"
          >
            {isLoading || isRefreshing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3" />
            )}
            {isRefreshing ? tSummary('refreshing') : tSummary('refresh')}
          </Button>
          <span className="text-xs text-gray-500 ml-auto">
            ({objectName.indexOf('__') > 0 ? objectName.split('__')[1] : objectName} - {recordId})
          </span>
        </div>
        <div className="flex-1 flex min-h-0 overflow-hidden p-2">
          <div
            className={`${isCanvas ? '' : 'p-3'} flex-1 overflow-y-auto min-h-0`}
            style={{ maxHeight: 'calc(100% - 1rem)' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader />
              </div>
            ) : data ? (
              <>
                <div className="prose max-w-none">
                  <MarkDownFormatter content={data.summary} />
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {tSummary('generatedAt', {
                    date: new Date(data.generated_at),
                    formatParams: {
                      date: {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        timeZone: userTimezone,
                      },
                    },
                  })}
                </div>
              </>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-red-600 mb-4">
                    {tSummary('errorLoadingSummary', { message: error.message })}
                  </p>
                  <Button onClick={handleRefresh} variant="outline">
                    {tSummary('tryAgain')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </>
    </div>
  );
}
